import React from 'react';
import { ArrowRight, MessageCircle } from 'lucide-react';
import { useLang } from '../../context/LanguageContext';
import { useSiteConfig } from '../../context/SiteConfigContext';

const Hero = () => {
  const { lang } = useLang();
  const { config } = useSiteConfig();
  const hero = config?.hero?.[lang];
  const primaryContact = (config?.contacts || []).find((c) => (c.showInLangs || []).includes(lang)) || (config?.contacts || [])[0];
  const whatsappNumber = primaryContact?.whatsapp || '596696396739';

  if (!hero) return <section className="bg-[#0e2a24] min-h-[400px]" />;

  const scrollTo = (id) => (e) => {
    e.preventDefault();
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section id="top" className="relative overflow-hidden bg-[#0e2a24] text-white">
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute -top-40 -right-40 w-[600px] h-[600px] rounded-full bg-[#134a3e]/60 blur-3xl" />
        <div className="absolute -bottom-40 -left-20 w-[500px] h-[500px] rounded-full bg-[#0a1f1a] blur-3xl" />
      </div>

      <div className="relative mx-auto max-w-7xl px-6 lg:px-10 pt-14 pb-20">
        <p className="text-[11px] tracking-[0.25em] uppercase text-white/50">{hero.tagline}</p>

        <h1 className="mt-6 font-serif text-4xl md:text-6xl leading-[1.05] tracking-tight max-w-3xl">
          {hero.title_1}{' '}
          <em className="not-italic" style={{ fontStyle: 'italic', color: '#e0b972' }}>{hero.title_2}</em>
        </h1>

        <p className="mt-6 max-w-2xl text-[15px] text-white/70 leading-relaxed">{hero.lead}</p>

        <div className="mt-8 flex flex-col sm:flex-row gap-3">
          <button
            onClick={scrollTo('circuits')}
            className="group inline-flex items-center gap-2 rounded-md bg-[#e89142] hover:bg-[#d97e2f] text-[#1a1a1a] font-medium px-5 py-3 text-sm transition-colors"
          >
            {hero.cta_primary}
            <ArrowRight size={16} className="group-hover:translate-x-0.5 transition-transform" />
          </button>
          <a
            href={`https://wa.me/${whatsappNumber}`}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-2 rounded-md border border-white/20 hover:border-white/40 text-white px-5 py-3 text-sm transition-colors"
          >
            <MessageCircle size={16} />
            {hero.cta_whatsapp}
          </a>
        </div>

        <div className="mt-14 rounded-lg border border-white/10 bg-white/[0.03] p-5">
          <p className="text-[10px] tracking-[0.25em] uppercase text-white/40">{hero.journey}</p>
          <div className="mt-4 relative h-[6px] rounded-full bg-white/10 overflow-hidden">
            <div className="absolute inset-y-0 left-0 w-[68%] rounded-full bg-gradient-to-r from-[#e0b972] via-[#e89142] to-[#e0b972]" />
            <div className="absolute top-1/2 -translate-y-1/2 left-0 w-2.5 h-2.5 rounded-full bg-[#e0b972]" />
            <div className="absolute top-1/2 -translate-y-1/2 left-[68%] w-2.5 h-2.5 rounded-full bg-[#e89142]" />
          </div>
          <div className="mt-3 flex justify-between text-[11px] text-white/60 font-mono">
            <span>08:30 · {hero.journey_start}</span>
            <span className="hidden sm:block">{hero.journey_mid}</span>
            <span>{hero.journey_end}</span>
          </div>
        </div>
      </div>

      <div className="relative border-t border-white/5 bg-[#0a231d]">
        <div className="mx-auto max-w-7xl px-6 lg:px-10 py-3 flex flex-wrap items-center gap-x-6 gap-y-2 text-[11px] text-white/50">
          {(hero.badges || []).map((b, i) => (
            <span key={i} className="inline-flex items-center gap-2">
              <span className="w-1 h-1 rounded-full bg-[#e0b972]" />
              {b}
            </span>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Hero;
